/**
 * Das Wenige, das die Ansichten teilen: der Ladezustand einer Liste und die
 * Zeile, die sagt, dass etwas nicht ging.
 *
 * Die Ansichten erfinden nichts: Antwortet die eigene API nicht oder nicht in der
 * erwarteten Form, steht hier der Wortlaut des Fehlers und keine leere Tabelle,
 * die wie „nichts angelegt" aussähe.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";

import { Icon } from "../design-system";

export type Loaded<T> =
  | { kind: "loading" }
  | { kind: "loaded"; value: T }
  | { kind: "failed"; message: string };

export interface Loading<T> {
  state: Loaded<T>;
  reload: () => void;
}

/**
 * Lädt einmal beim Erscheinen und danach auf Zuruf.
 *
 * Die Ladefunktion steht in einer Referenz, damit sie sich bei jedem Rendern
 * ändern darf, ohne einen neuen Ladevorgang auszulösen — sonst lüde eine Ansicht,
 * die ihre Ladefunktion inline schreibt, endlos.
 */
export function useLoaded<T>(load: () => Promise<T>): Loading<T> {
  const [state, setState] = useState<Loaded<T>>({ kind: "loading" });
  const [round, setRound] = useState(0);
  const latest = useRef(load);
  latest.current = load;

  useEffect(() => {
    let discarded = false;
    setState({ kind: "loading" });

    latest.current().then(
      (value) => {
        if (!discarded) {
          setState({ kind: "loaded", value });
        }
      },
      (error: unknown) => {
        if (!discarded) {
          setState({ kind: "failed", message: describe(error) });
        }
      },
    );

    return () => {
      discarded = true;
    };
  }, [round]);

  return { state, reload: () => setRound((previous) => previous + 1) };
}

export function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** Eine einzeilige Meldung — „wird geladen", „nicht verbunden", „noch nichts angelegt". */
export function Notice({
  text,
  glyph = "circle-alert",
  color = "var(--fg-6)",
}: {
  text: string;
  glyph?: string;
  color?: string;
}): ReactElement {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--sp-4)",
        padding: "var(--sp-6) 0",
        fontSize: "var(--fs-12)",
        color,
        textWrap: "pretty",
      }}
    >
      <Icon name={glyph} size={15} />
      <span>{text}</span>
    </div>
  );
}
